import React, { useState, useEffect, useRef, useMemo } from 'react';
import baseUrl from '../../base_url';
import axios from '../components/customAxios';
import toast from 'react-hot-toast';
import {
  Package,
  Plus,
  Search,
  Edit2,
  Trash2,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

const AdminProductsListingPage = () => {
  // State management
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 10;
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All'); // 'All' | 'active' | 'inactive'

  const [variantSearchTerm, setVariantSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Edit Modal State
  const [variantProducts, setVariantProducts] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    broadCategory: 'Jutexx',
    inventory: '',
    price: '',
    numericPrice: '',
    description: '',
    status: '',
    variants: [],
    articleCode: '',
    category: '',
    image: []
  });

  // Image editing state
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const fileInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch products
  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}api/products/data/all-products`);
        setProducts(response.data.data || []);
        setFilteredProducts(response.data.data || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        toast.error('Failed to load products');
        setLoading(false);
      }
    }

    fetchProducts();
  }, []);

  // Filter & Search
  useEffect(() => {
    let result = products;

    if (statusFilter !== 'All') {
      result = result.filter(p => (p.status || 'active').toLowerCase() === statusFilter.toLowerCase());
    }

    if (selectedCategory !== 'All') {
      result = result.filter(p =>
        (p.category && p.category.toLowerCase() === selectedCategory.toLowerCase()) ||
        (p.broadCategory && p.broadCategory.toLowerCase() === selectedCategory.toLowerCase())
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.title?.toLowerCase().includes(q) ||
        p.articleCode?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.broadCategory?.toLowerCase().includes(q)
      );
    }

    setFilteredProducts(result);
    setCurrentPage(1);
  }, [products, statusFilter, selectedCategory, searchTerm]);

  // Unique categories for filter dropdown
  const categoriesList = useMemo(() => {
    const cats = new Set();
    products.forEach(p => {
      if (p.category) cats.add(p.category);
      if (p.broadCategory) cats.add(p.broadCategory);
    });
    return Array.from(cats);
  }, [products]);

  // KPI Metrics
  const metrics = useMemo(() => {
    const total = products.length;
    const active = products.filter(p => (p.status || 'active') === 'active').length;
    const lowStock = products.filter(p => {
      const inv = Number(p.inventory) || 0;
      return inv > 0 && inv < 10;
    }).length;
    const outOfStock = products.filter(p => (Number(p.inventory) || 0) <= 0).length;

    return { total, active, lowStock, outOfStock };
  }, [products]);

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage) || 1;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Helper functions
  const extractVariantIds = (variants) => {
    if (!variants) return [];
    return variants.map(variant =>
      typeof variant === 'string' ? variant : variant._id
    );
  };

  const getVariantObjectsFromIds = (variantIds) => {
    return variantIds.map(id => {
      const foundProduct = products.find(p => p._id === id);
      return foundProduct || { _id: id, title: 'Unknown Product', image: [] };
    });
  };

  const generateFormattedPrice = (numericValue) => {
    return `£ ${numericValue.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatPrice = (product) => {
    if (product.price && typeof product.price === 'string') {
      return product.price;
    }
    const numericPrice = parseFloat(product.numericPrice || 0);
    if (isNaN(numericPrice)) return '£ 0.00';
    return `£ ${numericPrice.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Image handling
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newPreviewUrls = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      isNew: true,
      id: Date.now() + Math.random()
    }));

    setNewImages(prev => [...prev, ...newPreviewUrls]);
    setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
  };

  const uploadNewImages = async () => {
    if (newImages.length === 0) return [];

    setIsUploadingImages(true);
    const uploadedImages = [];

    try {
      for (const imageData of newImages) {
        const formData = new FormData();
        formData.append('images', imageData.file);
        formData.append('productId', editingProduct._id);

        const response = await axios.post(
          `${baseUrl}api/admin/upload-product-images`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
          }
        );

        const newImageUrls = response.data.data.urls.map((url, index) => ({
          src: url,
          id: Date.now() + Math.random() + index
        }));
        uploadedImages.push(...newImageUrls);
      }

      return uploadedImages;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    } finally {
      setIsUploadingImages(false);
    }
  };

  // Open edit modal
  const handleEditClick = (product) => {
    setEditingProduct(product);
    const variantIds = extractVariantIds(product.variants || []);

    setEditFormData({
      title: product.title || '',
      broadCategory: product.broadCategory || 'Jutexx',
      inventory: product.inventory || '0',
      price: product.price || '£ 0.00',
      numericPrice: product.numericPrice || 0,
      description: product.description || '',
      status: product.status || 'active',
      variants: variantIds,
      articleCode: product.articleCode || '',
      category: product.category || '',
      image: (product.image || []).map((img, index) => ({
        ...img,
        id: img._id || index,
        dataAltImage: index === 0 ? img.dataAltImage || img.src : undefined,
        alt: index === 0 ? img.alt || product.title : undefined,
        dataAltText: index === 0 ? img.dataAltText || product.title : undefined
      }))
    });

    const existingImages = (product.image || []).map((img, index) => ({
      ...img,
      url: img.src || img.dataAltImage,
      isNew: false,
      id: img._id || index
    }));
    setImagePreviewUrls(existingImages);

    setVariantProducts(getVariantObjectsFromIds(variantIds));
    setNewImages([]);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    setVariantProducts([]);
    setSearchResults([]);
    setImagePreviewUrls([]);
    setNewImages([]);

    imagePreviewUrls.forEach(img => URL.revokeObjectURL(img.url));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'numericPrice') {
      const numericValue = parseFloat(value) || 0;
      setEditFormData({
        ...editFormData,
        numericPrice: numericValue,
        price: generateFormattedPrice(numericValue)
      });
    } else {
      setEditFormData({
        ...editFormData,
        [name]: value
      });
    }
  };

  const handleVariantSearch = async () => {
    if (!variantSearchTerm.trim()) return;
    try {
      setIsSearching(true);
      const response = await axios.get(
        `${baseUrl}api/products/search/query?q=${encodeURIComponent(variantSearchTerm)}`
      );
      const currentId = editingProduct?._id;
      const currentVariantIds = editFormData.variants || [];
      const filtered = (response.data.data || []).filter(
        p => p._id !== currentId && !currentVariantIds.includes(p._id)
      );
      setSearchResults(filtered);
    } catch (err) {
      console.error('Error searching variants:', err);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const addVariant = (product) => {
    if (!editFormData.variants.includes(product._id)) {
      setEditFormData(prev => ({
        ...prev,
        variants: [...prev.variants, product._id]
      }));
      setVariantProducts(prev => [...prev, product]);
      setSearchResults(prev => prev.filter(p => p._id !== product._id));
      toast.success(`Added ${product.title} as variant`);
    }
  };

  const removeVariant = (variantId) => {
    setEditFormData(prev => ({
      ...prev,
      variants: prev.variants.filter(id => id !== variantId)
    }));
    setVariantProducts(prev => prev.filter(p => p._id !== variantId));
  };

  const handleDeleteProduct = async (product) => {
    const id = product._id || product.id;
    if (window.confirm(`Are you sure you want to delete "${product.title}"? This cannot be undone.`)) {
      try {
        await axios.post(`${baseUrl}api/admin/delete-product/${id}`, {}, { withCredentials: true });
        setProducts(prev => prev.filter(p => (p._id || p.id) !== id));
        toast.success('Product deleted successfully');
      } catch (err) {
        console.error('Error deleting product:', err);
        toast.error('Failed to delete product');
      }
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      let finalImages = [...(editFormData.image || [])];
      const hasNewImages = newImages.length > 0;

      if (hasNewImages) {
        const uploadedImageUrls = await uploadNewImages();
        const formattedUploadedImages = uploadedImageUrls.map(img => ({
          src: img.src,
          id: img.id
        }));
        finalImages = [...finalImages, ...formattedUploadedImages];
      }

      const updatedFields = {};
      Object.keys(editFormData).forEach((key) => {
        if (key === 'variants') {
          const currentVariantIds = extractVariantIds(editingProduct.variants || []);
          const newVariantIds = editFormData.variants;
          if (JSON.stringify(currentVariantIds.sort()) !== JSON.stringify([...newVariantIds].sort())) {
            updatedFields.variants = newVariantIds;
          }
        } else if (key === 'image') {
          if (hasNewImages) {
            updatedFields.image = finalImages.map(img => ({ src: img.src }));
          }
        } else if (key === 'numericPrice') {
          const newNumericPrice = parseFloat(editFormData.numericPrice);
          const originalNumericPrice = parseFloat(editingProduct.numericPrice || 0);
          if (!isNaN(newNumericPrice) && newNumericPrice !== originalNumericPrice) {
            updatedFields.numericPrice = newNumericPrice;
            updatedFields.price = generateFormattedPrice(newNumericPrice);
          }
        } else if (key === 'price') {
          return;
        } else {
          const currentValue = editFormData[key];
          const originalValue = editingProduct[key];
          if (currentValue !== (originalValue || '')) {
            updatedFields[key] = currentValue;
          }
        }
      });

      if (Object.keys(updatedFields).length === 0 && !hasNewImages) {
        setIsSaving(false);
        handleCloseModal();
        toast('No changes detected', { icon: 'ℹ️' });
        return;
      }

      const response = await axios.post(
        `${baseUrl}api/admin/edit-product/${editingProduct.slug}`,
        updatedFields,
        { withCredentials: true }
      );

      const updatedProduct = response.data.data;
      setProducts(prev =>
        prev.map(p => (p._id === editingProduct._id ? updatedProduct : p))
      );

      setNewImages([]);
      handleCloseModal();
      toast.success('Product updated successfully');
    } catch (err) {
      console.error('Error updating product:', err);
      toast.error(`Failed to update product: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Products Catalog</h1>
            <span className="text-xs bg-amber-50 text-amber-800 border border-amber-200/80 font-mono px-2 py-0.5 rounded-full font-semibold">
              {metrics.total} SKUs
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">
            Manage SKU specifications, active listings, pricing, stock levels, and product variants.
          </p>
        </div>

        <a
          href="/add-products"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 transition-colors shadow-xs shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
          Add New Product
        </a>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Total Products</div>
          <div className="text-2xl font-bold text-stone-900 mt-1 font-mono">{metrics.total}</div>
          <div className="text-[11px] text-stone-500 mt-1">Catalog items</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Active Listings</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1 font-mono">{metrics.active}</div>
          <div className="text-[11px] text-stone-500 mt-1">Visible on storefront</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Low Stock Warning</div>
          <div className="text-2xl font-bold text-amber-600 mt-1 font-mono">{metrics.lowStock}</div>
          <div className="text-[11px] text-stone-500 mt-1">Under 10 units left</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Depleted (0 Units)</div>
          <div className="text-2xl font-bold text-rose-600 mt-1 font-mono">{metrics.outOfStock}</div>
          <div className="text-[11px] text-stone-500 mt-1">Unavailable for orders</div>
        </div>
      </div>

      {/* Main Workspace Table */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        {/* Controls Toolbar */}
        <div className="p-4 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Status Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
            {[
              { id: 'All', label: 'All Products', count: products.length },
              { id: 'active', label: 'Active', count: metrics.active },
              { id: 'inactive', label: 'Inactive / Draft', count: products.length - metrics.active }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  statusFilter === tab.id
                    ? 'bg-stone-900 text-white font-semibold shadow-xs'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[10px] ${
                  statusFilter === tab.id ? 'bg-stone-800 text-stone-200' : 'bg-stone-100 text-stone-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search & Category Filter */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-60">
              <input
                type="text"
                placeholder="Search title, SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-stone-50 border border-stone-200 rounded-lg py-1.5 pl-8 pr-3 text-xs text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:bg-white transition-all"
              />
              <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-stone-50 border border-stone-200 rounded-lg py-1.5 px-2.5 text-xs text-stone-700 focus:outline-none focus:ring-1 focus:ring-amber-500"
            >
              <option value="All">All Categories</option>
              {categoriesList.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading / Empty States */}
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-amber-600 mb-3"></div>
            <p className="text-xs text-stone-500 font-medium">Loading catalog products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-8 h-8 text-stone-300 mx-auto mb-2" />
            <p className="text-xs text-stone-500 font-medium">No products found matching your search.</p>
            <p className="text-[11px] text-stone-400 mt-1">Try selecting a different category or clearing the search bar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 text-[11px] font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-100">
                  <th className="py-3 px-4">Product Details</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Price</th>
                  <th className="py-3 px-4">Inventory</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 text-xs">
                {currentProducts.map((product) => {
                  const inv = Number(product.inventory) || 0;
                  const isActive = (product.status || 'active') === 'active';
                  const firstImg = product.image && product.image[0] ? product.image[0].src : null;

                  return (
                    <tr key={product._id || product.id} className="hover:bg-stone-50/70 transition-colors">
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-lg bg-stone-100 border border-stone-200 overflow-hidden shrink-0 flex items-center justify-center">
                            {firstImg ? (
                              <img
                                src={firstImg}
                                alt={product.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <ImageIcon className="w-5 h-5 text-stone-300" />
                            )}
                          </div>
                          <div className="min-w-0 max-w-xs sm:max-w-md">
                            <a
                              href={`/productpage/${product.slug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="font-semibold text-stone-900 hover:text-amber-800 transition-colors truncate block text-xs"
                            >
                              {product.title}
                            </a>
                            <div className="text-[11px] text-stone-400 font-mono mt-0.5">
                              SKU: {product.articleCode || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="inline-block px-2.5 py-1 rounded-md bg-stone-100 text-stone-700 text-[11px] font-medium border border-stone-200/60">
                          {product.category || product.broadCategory || 'General'}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap font-mono font-semibold text-stone-900">
                        {formatPrice(product)}
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 font-mono">
                          <span className={`w-2 h-2 rounded-full ${
                            inv > 10 ? 'bg-emerald-500' : inv > 0 ? 'bg-amber-500' : 'bg-rose-500'
                          }`} />
                          <span className={`font-semibold ${
                            inv <= 0 ? 'text-rose-600' : inv < 10 ? 'text-amber-600' : 'text-stone-800'
                          }`}>
                            {inv} in stock
                          </span>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${
                          isActive
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-stone-100 text-stone-600 border-stone-200'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-stone-400'}`} />
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEditClick(product)}
                            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"
                            title="Edit Product"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <a
                            href={`/productpage/${product.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 rounded-lg text-stone-400 hover:text-amber-700 hover:bg-stone-100 transition-colors inline-block"
                            title="View on Store"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="p-1.5 rounded-lg text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                            title="Delete Product"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Bar */}
        {filteredProducts.length > 0 && (
          <div className="p-4 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-stone-500">
            <div>
              Showing <span className="font-semibold text-stone-800">{indexOfFirstProduct + 1}</span> to{' '}
              <span className="font-semibold text-stone-800">
                {Math.min(indexOfLastProduct, filteredProducts.length)}
              </span>{' '}
              of <span className="font-semibold text-stone-800">{filteredProducts.length}</span> products
            </div>

            <div className="flex items-center gap-1 self-center">
              <button
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="First Page"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              <span className="px-3 py-1 font-mono text-stone-700 font-semibold">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Product Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-stone-100 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700">
                  <Edit2 className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-stone-900">Edit Product: {editingProduct?.title}</h3>
                  <p className="text-[11px] text-stone-500">Update specifications, pricing, inventory, and variants</p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveChanges} className="p-6 overflow-y-auto space-y-6 text-xs flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div className="md:col-span-2">
                  <label className="block font-semibold text-stone-700 mb-1">Product Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={editFormData.title}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>

                {/* SKU */}
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Article Code (SKU)</label>
                  <input
                    type="text"
                    name="articleCode"
                    value={editFormData.articleCode}
                    onChange={handleInputChange}
                    className="w-full font-mono rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Category</label>
                  <input
                    type="text"
                    name="category"
                    value={editFormData.category}
                    onChange={handleInputChange}
                    placeholder="e.g. Cleansers, Face Care"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Price (£)</label>
                  <div className="space-y-1">
                    <input
                      type="number"
                      name="numericPrice"
                      step="0.01"
                      min="0"
                      value={editFormData.numericPrice}
                      onChange={handleInputChange}
                      className="w-full font-mono font-semibold rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    />
                    <div className="text-[11px] text-stone-400">Formatted: {editFormData.price}</div>
                  </div>
                </div>

                {/* Inventory */}
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Stock Level (Units)</label>
                  <input
                    type="number"
                    name="inventory"
                    min="0"
                    value={editFormData.inventory}
                    onChange={handleInputChange}
                    className="w-full font-mono rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Catalog Status</label>
                  <select
                    name="status"
                    value={editFormData.status}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  >
                    <option value="active">Active (Visible)</option>
                    <option value="inactive">Inactive (Draft)</option>
                  </select>
                </div>

                {/* Broad Category */}
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Broad Category</label>
                  <input
                    type="text"
                    name="broadCategory"
                    value={editFormData.broadCategory}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block font-semibold text-stone-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    rows="3"
                    value={editFormData.description}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="Enter product description..."
                  />
                </div>

                {/* Images */}
                <div className="md:col-span-2 pt-2 border-t border-stone-100">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-stone-800">Product Images</h4>
                      <p className="text-[11px] text-stone-400">First image is used as thumbnail</p>
                    </div>
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        multiple
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 font-medium transition-colors text-xs"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Upload Images
                      </button>
                    </div>
                  </div>

                  {imagePreviewUrls.length > 0 ? (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                      {imagePreviewUrls.map((imageData, index) => (
                        <div key={imageData.id || index} className="relative aspect-square rounded-lg overflow-hidden border border-stone-200 bg-stone-100">
                          <img
                            src={imageData.url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          <span className={`absolute top-1 left-1 text-[10px] font-bold px-1.5 py-0.2 rounded ${
                            imageData.isNew ? 'bg-amber-600 text-white' : 'bg-stone-900/70 text-white'
                          }`}>
                            {imageData.isNew ? 'New' : `#${index + 1}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center border-2 border-dashed border-stone-200 rounded-xl text-stone-400">
                      No images uploaded.
                    </div>
                  )}
                </div>

                {/* Variants */}
                <div className="md:col-span-2 pt-2 border-t border-stone-100">
                  <h4 className="font-semibold text-stone-800 mb-2">Connected Variants ({variantProducts.length})</h4>

                  {/* Search for variants */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      placeholder="Search other products to link as variants..."
                      value={variantSearchTerm}
                      onChange={(e) => setVariantSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleVariantSearch())}
                      className="flex-1 rounded-lg border border-stone-200 px-3 py-1.5 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    />
                    <button
                      type="button"
                      onClick={handleVariantSearch}
                      disabled={isSearching || !variantSearchTerm.trim()}
                      className="px-3 py-1.5 rounded-lg bg-stone-900 text-white font-semibold hover:bg-stone-800 disabled:opacity-50 transition-colors"
                    >
                      {isSearching ? 'Searching...' : 'Search'}
                    </button>
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="mb-3 border border-stone-200 rounded-xl p-2 bg-stone-50 max-h-40 overflow-y-auto space-y-1.5">
                      {searchResults.map(p => (
                        <div key={p._id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-stone-100">
                          <span className="truncate max-w-xs font-medium text-stone-800">{p.title}</span>
                          <button
                            type="button"
                            onClick={() => addVariant(p)}
                            className="px-2.5 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 font-semibold"
                          >
                            + Link
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Linked variants */}
                  {variantProducts.length > 0 ? (
                    <div className="space-y-1.5">
                      {variantProducts.map((variant, index) => (
                        <div key={variant._id} className="flex items-center justify-between p-2 rounded-lg bg-stone-50 border border-stone-200/80">
                          <span className="font-medium text-stone-800 truncate max-w-xs">
                            {variant.title} <span className="text-stone-400 font-normal">#{index + 1}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeVariant(variant._id)}
                            className="text-stone-400 hover:text-rose-600 p-1"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[11px] text-stone-400 italic">No variants linked to this product.</div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-2 pt-4 border-t border-stone-100">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || isUploadingImages}
                  className="px-4 py-2 rounded-lg bg-stone-900 text-white font-semibold hover:bg-stone-800 disabled:opacity-50 transition-colors shadow-xs"
                >
                  {isSaving ? 'Saving Changes...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsListingPage;